/**
 * Country list — ISO 3166-1 alpha-2 codes + Italian names.
 *
 * Scope: UN member states + a few widely-recognised dependencies that
 * commonly show up on Italian residency/legal documents (e.g. Holy See,
 * Hong Kong, Taiwan, Palestine). Italia is pinned at the top of the
 * list — it is by far the most common pick for an Italian backoffice.
 *
 * Why a hand-curated list (not an i18n library):
 * - The dependency footprint of `i18n-iso-countries` + locales is
 *   ~150 kB minified. We need ~6 kB of strings. The list is stable
 *   (changes maybe once a decade when a country renames) so the
 *   maintenance burden is effectively zero.
 * - Italian names are operator-facing on this backoffice; we don't
 *   need locale switching here.
 *
 * If a country is missing, just add the row — keep the alphabetical
 * order (after the pinned IT entry).
 */

export interface Country {
  /** ISO 3166-1 alpha-2 (uppercase). */
  iso2: string;
  /** Italian name as it should appear in the picker. */
  name: string;
}

export const COUNTRIES: ReadonlyArray<Country> = [
  // Pinned default — most common pick for an Italian backoffice.
  { iso2: 'IT', name: 'Italia' },

  // Alphabetical from here on.
  { iso2: 'AF', name: 'Afghanistan' },
  { iso2: 'AL', name: 'Albania' },
  { iso2: 'DZ', name: 'Algeria' },
  { iso2: 'AD', name: 'Andorra' },
  { iso2: 'AO', name: 'Angola' },
  { iso2: 'AI', name: 'Anguilla' },
  { iso2: 'AG', name: 'Antigua e Barbuda' },
  { iso2: 'SA', name: 'Arabia Saudita' },
  { iso2: 'AR', name: 'Argentina' },
  { iso2: 'AM', name: 'Armenia' },
  { iso2: 'AW', name: 'Aruba' },
  { iso2: 'AU', name: 'Australia' },
  { iso2: 'AT', name: 'Austria' },
  { iso2: 'AZ', name: 'Azerbaigian' },
  { iso2: 'BS', name: 'Bahamas' },
  { iso2: 'BH', name: 'Bahrein' },
  { iso2: 'BD', name: 'Bangladesh' },
  { iso2: 'BB', name: 'Barbados' },
  { iso2: 'BE', name: 'Belgio' },
  { iso2: 'BZ', name: 'Belize' },
  { iso2: 'BJ', name: 'Benin' },
  { iso2: 'BM', name: 'Bermuda' },
  { iso2: 'BT', name: 'Bhutan' },
  { iso2: 'BY', name: 'Bielorussia' },
  { iso2: 'BO', name: 'Bolivia' },
  { iso2: 'BA', name: 'Bosnia ed Erzegovina' },
  { iso2: 'BW', name: 'Botswana' },
  { iso2: 'BR', name: 'Brasile' },
  { iso2: 'BN', name: 'Brunei' },
  { iso2: 'BG', name: 'Bulgaria' },
  { iso2: 'BF', name: 'Burkina Faso' },
  { iso2: 'BI', name: 'Burundi' },
  { iso2: 'KH', name: 'Cambogia' },
  { iso2: 'CM', name: 'Camerun' },
  { iso2: 'CA', name: 'Canada' },
  { iso2: 'CV', name: 'Capo Verde' },
  { iso2: 'TD', name: 'Ciad' },
  { iso2: 'CL', name: 'Cile' },
  { iso2: 'CN', name: 'Cina' },
  { iso2: 'CY', name: 'Cipro' },
  { iso2: 'VA', name: 'Città del Vaticano' },
  { iso2: 'CO', name: 'Colombia' },
  { iso2: 'KM', name: 'Comore' },
  { iso2: 'KP', name: 'Corea del Nord' },
  { iso2: 'KR', name: 'Corea del Sud' },
  { iso2: 'CI', name: "Costa d'Avorio" },
  { iso2: 'CR', name: 'Costa Rica' },
  { iso2: 'HR', name: 'Croazia' },
  { iso2: 'CU', name: 'Cuba' },
  { iso2: 'CW', name: 'Curaçao' },
  { iso2: 'DK', name: 'Danimarca' },
  { iso2: 'DM', name: 'Dominica' },
  { iso2: 'EC', name: 'Ecuador' },
  { iso2: 'EG', name: 'Egitto' },
  { iso2: 'SV', name: 'El Salvador' },
  { iso2: 'AE', name: 'Emirati Arabi Uniti' },
  { iso2: 'ER', name: 'Eritrea' },
  { iso2: 'EE', name: 'Estonia' },
  { iso2: 'ET', name: 'Etiopia' },
  { iso2: 'FJ', name: 'Figi' },
  { iso2: 'PH', name: 'Filippine' },
  { iso2: 'FI', name: 'Finlandia' },
  { iso2: 'FR', name: 'Francia' },
  { iso2: 'GA', name: 'Gabon' },
  { iso2: 'GM', name: 'Gambia' },
  { iso2: 'GE', name: 'Georgia' },
  { iso2: 'DE', name: 'Germania' },
  { iso2: 'GH', name: 'Ghana' },
  { iso2: 'JM', name: 'Giamaica' },
  { iso2: 'JP', name: 'Giappone' },
  { iso2: 'GI', name: 'Gibilterra' },
  { iso2: 'DJ', name: 'Gibuti' },
  { iso2: 'JO', name: 'Giordania' },
  { iso2: 'GR', name: 'Grecia' },
  { iso2: 'GD', name: 'Grenada' },
  { iso2: 'GL', name: 'Groenlandia' },
  { iso2: 'GP', name: 'Guadalupa' },
  { iso2: 'GU', name: 'Guam' },
  { iso2: 'GT', name: 'Guatemala' },
  { iso2: 'GG', name: 'Guernsey' },
  { iso2: 'GN', name: 'Guinea' },
  { iso2: 'GW', name: 'Guinea-Bissau' },
  { iso2: 'GQ', name: 'Guinea Equatoriale' },
  { iso2: 'GY', name: 'Guyana' },
  { iso2: 'GF', name: 'Guyana francese' },
  { iso2: 'HT', name: 'Haiti' },
  { iso2: 'HN', name: 'Honduras' },
  { iso2: 'HK', name: 'Hong Kong' },
  { iso2: 'IN', name: 'India' },
  { iso2: 'ID', name: 'Indonesia' },
  { iso2: 'IR', name: 'Iran' },
  { iso2: 'IQ', name: 'Iraq' },
  { iso2: 'IE', name: 'Irlanda' },
  { iso2: 'IS', name: 'Islanda' },
  { iso2: 'BV', name: 'Isola Bouvet' },
  { iso2: 'IM', name: 'Isola di Man' },
  { iso2: 'CX', name: 'Isola di Natale' },
  { iso2: 'NF', name: 'Isola Norfolk' },
  { iso2: 'AX', name: 'Isole Åland' },
  { iso2: 'KY', name: 'Isole Cayman' },
  { iso2: 'CC', name: 'Isole Cocos' },
  { iso2: 'CK', name: 'Isole Cook' },
  { iso2: 'FO', name: 'Isole Fær Øer' },
  { iso2: 'FK', name: 'Isole Falkland' },
  { iso2: 'MP', name: 'Isole Marianne Settentrionali' },
  { iso2: 'MH', name: 'Isole Marshall' },
  { iso2: 'PN', name: 'Isole Pitcairn' },
  { iso2: 'SB', name: 'Isole Salomone' },
  { iso2: 'TC', name: 'Isole Turks e Caicos' },
  { iso2: 'VG', name: 'Isole Vergini britanniche' },
  { iso2: 'VI', name: 'Isole Vergini americane' },
  { iso2: 'IL', name: 'Israele' },
  { iso2: 'JE', name: 'Jersey' },
  { iso2: 'KZ', name: 'Kazakistan' },
  { iso2: 'KE', name: 'Kenya' },
  { iso2: 'KG', name: 'Kirghizistan' },
  { iso2: 'KI', name: 'Kiribati' },
  { iso2: 'XK', name: 'Kosovo' },
  { iso2: 'KW', name: 'Kuwait' },
  { iso2: 'LA', name: 'Laos' },
  { iso2: 'LS', name: 'Lesotho' },
  { iso2: 'LV', name: 'Lettonia' },
  { iso2: 'LB', name: 'Libano' },
  { iso2: 'LR', name: 'Liberia' },
  { iso2: 'LY', name: 'Libia' },
  { iso2: 'LI', name: 'Liechtenstein' },
  { iso2: 'LT', name: 'Lituania' },
  { iso2: 'LU', name: 'Lussemburgo' },
  { iso2: 'MO', name: 'Macao' },
  { iso2: 'MK', name: 'Macedonia del Nord' },
  { iso2: 'MG', name: 'Madagascar' },
  { iso2: 'MW', name: 'Malawi' },
  { iso2: 'MY', name: 'Malesia' },
  { iso2: 'MV', name: 'Maldive' },
  { iso2: 'ML', name: 'Mali' },
  { iso2: 'MT', name: 'Malta' },
  { iso2: 'MA', name: 'Marocco' },
  { iso2: 'MQ', name: 'Martinica' },
  { iso2: 'MR', name: 'Mauritania' },
  { iso2: 'MU', name: 'Mauritius' },
  { iso2: 'YT', name: 'Mayotte' },
  { iso2: 'MX', name: 'Messico' },
  { iso2: 'FM', name: 'Micronesia' },
  { iso2: 'MD', name: 'Moldavia' },
  { iso2: 'MC', name: 'Monaco' },
  { iso2: 'MN', name: 'Mongolia' },
  { iso2: 'ME', name: 'Montenegro' },
  { iso2: 'MS', name: 'Montserrat' },
  { iso2: 'MZ', name: 'Mozambico' },
  { iso2: 'MM', name: 'Myanmar' },
  { iso2: 'NA', name: 'Namibia' },
  { iso2: 'NR', name: 'Nauru' },
  { iso2: 'NP', name: 'Nepal' },
  { iso2: 'NI', name: 'Nicaragua' },
  { iso2: 'NE', name: 'Niger' },
  { iso2: 'NG', name: 'Nigeria' },
  { iso2: 'NU', name: 'Niue' },
  { iso2: 'NO', name: 'Norvegia' },
  { iso2: 'NC', name: 'Nuova Caledonia' },
  { iso2: 'NZ', name: 'Nuova Zelanda' },
  { iso2: 'OM', name: 'Oman' },
  { iso2: 'NL', name: 'Paesi Bassi' },
  { iso2: 'PK', name: 'Pakistan' },
  { iso2: 'PW', name: 'Palau' },
  { iso2: 'PS', name: 'Palestina' },
  { iso2: 'PA', name: 'Panama' },
  { iso2: 'PG', name: 'Papua Nuova Guinea' },
  { iso2: 'PY', name: 'Paraguay' },
  { iso2: 'PE', name: 'Perù' },
  { iso2: 'PF', name: 'Polinesia francese' },
  { iso2: 'PL', name: 'Polonia' },
  { iso2: 'PR', name: 'Porto Rico' },
  { iso2: 'PT', name: 'Portogallo' },
  { iso2: 'QA', name: 'Qatar' },
  { iso2: 'GB', name: 'Regno Unito' },
  { iso2: 'CZ', name: 'Repubblica Ceca' },
  { iso2: 'CF', name: 'Repubblica Centrafricana' },
  { iso2: 'CG', name: 'Repubblica del Congo' },
  { iso2: 'CD', name: 'Repubblica Democratica del Congo' },
  { iso2: 'DO', name: 'Repubblica Dominicana' },
  { iso2: 'RE', name: 'Riunione' },
  { iso2: 'RO', name: 'Romania' },
  { iso2: 'RW', name: 'Ruanda' },
  { iso2: 'RU', name: 'Russia' },
  { iso2: 'EH', name: 'Sahara Occidentale' },
  { iso2: 'KN', name: 'Saint Kitts e Nevis' },
  { iso2: 'LC', name: 'Saint Lucia' },
  { iso2: 'VC', name: 'Saint Vincent e Grenadine' },
  { iso2: 'BL', name: 'Saint-Barthélemy' },
  { iso2: 'MF', name: 'Saint-Martin' },
  { iso2: 'PM', name: 'Saint-Pierre e Miquelon' },
  { iso2: 'WS', name: 'Samoa' },
  { iso2: 'AS', name: 'Samoa americane' },
  { iso2: 'SM', name: 'San Marino' },
  { iso2: 'SH', name: "Sant'Elena" },
  { iso2: 'ST', name: 'São Tomé e Príncipe' },
  { iso2: 'SN', name: 'Senegal' },
  { iso2: 'RS', name: 'Serbia' },
  { iso2: 'SC', name: 'Seychelles' },
  { iso2: 'SL', name: 'Sierra Leone' },
  { iso2: 'SG', name: 'Singapore' },
  { iso2: 'SX', name: 'Sint Maarten' },
  { iso2: 'SY', name: 'Siria' },
  { iso2: 'SK', name: 'Slovacchia' },
  { iso2: 'SI', name: 'Slovenia' },
  { iso2: 'SO', name: 'Somalia' },
  { iso2: 'ES', name: 'Spagna' },
  { iso2: 'LK', name: 'Sri Lanka' },
  { iso2: 'US', name: "Stati Uniti d'America" },
  { iso2: 'ZA', name: 'Sudafrica' },
  { iso2: 'SD', name: 'Sudan' },
  { iso2: 'SS', name: 'Sudan del Sud' },
  { iso2: 'SR', name: 'Suriname' },
  { iso2: 'SE', name: 'Svezia' },
  { iso2: 'CH', name: 'Svizzera' },
  { iso2: 'SZ', name: 'Eswatini' },
  { iso2: 'TJ', name: 'Tagikistan' },
  { iso2: 'TW', name: 'Taiwan' },
  { iso2: 'TZ', name: 'Tanzania' },
  { iso2: 'TH', name: 'Thailandia' },
  { iso2: 'TL', name: 'Timor Est' },
  { iso2: 'TG', name: 'Togo' },
  { iso2: 'TK', name: 'Tokelau' },
  { iso2: 'TO', name: 'Tonga' },
  { iso2: 'TT', name: 'Trinidad e Tobago' },
  { iso2: 'TN', name: 'Tunisia' },
  { iso2: 'TR', name: 'Turchia' },
  { iso2: 'TM', name: 'Turkmenistan' },
  { iso2: 'TV', name: 'Tuvalu' },
  { iso2: 'UA', name: 'Ucraina' },
  { iso2: 'UG', name: 'Uganda' },
  { iso2: 'HU', name: 'Ungheria' },
  { iso2: 'UY', name: 'Uruguay' },
  { iso2: 'UZ', name: 'Uzbekistan' },
  { iso2: 'VU', name: 'Vanuatu' },
  { iso2: 'VE', name: 'Venezuela' },
  { iso2: 'VN', name: 'Vietnam' },
  { iso2: 'WF', name: 'Wallis e Futuna' },
  { iso2: 'YE', name: 'Yemen' },
  { iso2: 'ZM', name: 'Zambia' },
  { iso2: 'ZW', name: 'Zimbabwe' },
];

export function getCountryByIso(iso2: string | null | undefined): Country | undefined {
  if (!iso2) return undefined;
  const upper = iso2.toUpperCase();
  return COUNTRIES.find((c) => c.iso2 === upper);
}

/**
 * Returns a flag image URL for the given ISO2 code. Uses the public
 * `flagcdn.com` CDN (Lipis flag-icons project, MIT). SVG variant for
 * crisp rendering on retina/4K displays.
 *
 * Note: this is an external dependency. If we ever need to go offline-
 * first or comply with a stricter CSP, mirror the SVGs locally under
 * `apps/web-backoffice/public/flags/<iso>.svg` and switch the helper.
 */
export function getFlagUrl(iso2: string): string {
  return `https://flagcdn.com/${iso2.toLowerCase()}.svg`;
}
